// The care period as a clock. Anchored on `cms_alignment_date` — never enrollment, which runs
// 8–40 days ahead of it and would disagree with the CMS deadlines on the same screen.
// ⚠️ `pct` is CALENDAR TIME, not attainment: it draws the arc and is never rendered as a number.

import {
  baselineWindow, quarterlyWindow, finalWindow, milestoneReportDates, reportingActions,
  formatReportDate, type CmsCarePeriod, type CmsSubmissionLike, type CmsMilestoneSlot,
} from './cmsWindows';
import { ACTIVE_CARE_STATUSES, type CarePeriodStatus } from './carePeriod';
import { completedTemplateAt, isTaskOverdue, templateCodeOf } from './task';

const DAY = 86_400_000;
export const CARE_PERIOD_DAYS = 365;
const COMPLETION_TEMPLATE = 'rpt_care_completion_report';

function ms(iso?: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
}

export type DayCounterState = 'unknown' | 'not_aligned' | 'in_period' | 'final_window' | 'closed';

export interface DayCounter {
  state: DayCounterState;
  /** 1-based day of the care period, or null before alignment. */
  day: number | null;
  total: typeof CARE_PERIOD_DAYS;
  daysLeft: number | null;
  /** 0–1, for the ring's arc only. Never rendered as text. */
  pct: number | null;
  alignedAt: number | null;
  endsAt: number | null;
  finalCloseAt: number | null;
  enrolledDaysAgo: number | null;
  label: string;
  sublabel: string;
}

export interface CarePeriodInput extends CmsCarePeriod {
  status?: CarePeriodStatus | string | null;
}

const ACTIVE = new Set<string>(ACTIVE_CARE_STATUSES);

// Day 1 is the alignment day. 366–425 is `final_window`: complete, but still filable.
export function dayCounter(cp: CarePeriodInput | null | undefined, now: number = Date.now()): DayCounter {
  const base: DayCounter = {
    state: 'unknown',
    day: null,
    total: CARE_PERIOD_DAYS,
    daysLeft: null,
    pct: null,
    alignedAt: null,
    endsAt: null,
    finalCloseAt: null,
    enrolledDaysAgo: null,
    label: 'Care period',
    sublabel: '',
  };
  if (!cp) return base;

  const enrolledAt = ms(cp.start_date);
  const enrolledDaysAgo = enrolledAt == null ? null : Math.max(0, Math.floor((now - enrolledAt) / DAY));
  const alignedAt = ms(cp.cms_alignment_date);

  if (alignedAt == null) {
    return {
      ...base,
      state: 'not_aligned',
      enrolledDaysAgo,
      label: 'Not yet aligned',
      sublabel: enrolledDaysAgo == null
        ? 'the care period starts at alignment'
        : `enrolled ${enrolledDaysAgo} ${enrolledDaysAgo === 1 ? 'day' : 'days'} ago`,
    };
  }

  const endsAt = alignedAt + CARE_PERIOD_DAYS * DAY;
  const finalCloseAt = alignedAt + 425 * DAY;
  const elapsed = Math.floor((now - alignedAt) / DAY) + 1;
  const common = {
    ...base, enrolledDaysAgo, alignedAt, endsAt, finalCloseAt,
  };

  if (now > finalCloseAt || (cp.status != null && !ACTIVE.has(String(cp.status)))) {
    // A patient withdrawn on day 100 never reached 365, so neither the number nor the arc may say
    // so; only a period that ran out of calendar fills the ring.
    const reached = Math.min(CARE_PERIOD_DAYS, Math.max(1, elapsed));
    return {
      ...common,
      state: 'closed',
      day: reached,
      daysLeft: 0,
      pct: reached / CARE_PERIOD_DAYS,
      label: 'Period closed',
      sublabel: `ended on day ${reached} · aligned ${formatReportDate(alignedAt)}`,
    };
  }
  if (elapsed > CARE_PERIOD_DAYS) {
    return {
      ...common,
      state: 'final_window',
      day: CARE_PERIOD_DAYS,
      daysLeft: 0,
      pct: 1,
      label: 'Period complete',
      sublabel: `final report window closes ${formatReportDate(finalCloseAt)}`,
    };
  }
  const day = Math.min(CARE_PERIOD_DAYS, Math.max(1, elapsed));
  const daysLeft = CARE_PERIOD_DAYS - day;
  return {
    ...common,
    state: 'in_period',
    day,
    daysLeft,
    pct: day / CARE_PERIOD_DAYS,
    label: `Day ${day} of ${CARE_PERIOD_DAYS}`,
    sublabel: `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left · aligned ${formatReportDate(alignedAt)}, ends ${formatReportDate(endsAt)}`,
  };
}

export type ClockSlot = CmsMilestoneSlot | 'completion';
export type ClockState = 'submitted' | 'open' | 'upcoming' | 'overdue' | 'blocked' | 'na';

export interface ClockAction {
  kind: 'baseline' | 'quarterly' | 'final';
  enabled: boolean;
  reason: string | null;
}

export interface ClockEntry {
  slot: ClockSlot;
  label: string;
  state: ClockState;
  submittedAt: number | null;
  opensAt: number | null;
  dueAt: number | null;
  hint: string;
  /** The submit button this milestone carries, or null when it has none. */
  action: ClockAction | null;
}

const Q_SLOTS: CmsMilestoneSlot[] = ['q1', 'q2', 'q3'];
const SLOT_LABEL: Record<ClockSlot, string> = {
  baseline: 'Baseline', q1: 'Q1', q2: 'Q2', q3: 'Q3', final: 'Final', completion: 'Completion',
};

export interface ClockTask {
  status?: string;
  due_at?: string | null;
  completed_at?: string | null;
  context_json?: unknown;
  template?: { template_code?: string } | null;
  template_code?: string | null;
}

// Only the FIRST unsubmitted quarterly gets a window — the rolling 70–110 day rule makes a later
// quarter's dates unknowable, so the others name what they wait for rather than invent one.
export function reportingClock(
  cp: CarePeriodInput | null | undefined,
  subs: readonly CmsSubmissionLike[] | null | undefined,
  tasks: readonly ClockTask[] | null | undefined,
  now: number = Date.now(),
): ClockEntry[] {
  const submitted = milestoneReportDates(subs);
  const actions = reportingActions(cp, subs, now);
  // ONLY an `active` period may file. Nothing downstream re-checks it — cmsWindows derives purely
  // from `cms_alignment_date`, and neither WF24 (unalign) nor WF67 (withdraw) ever clears that
  // field — so without this an unaligned or withdrawn patient still inside the +60 day window
  // renders an ENABLED "Submit Baseline". `pending_unalignment` is excluded too: it is active care
  // for the day counter, but the old card gave it no buttons and one is already in flight.
  const canFile = String(cp?.status ?? '') === 'active';
  const actionFor = (kind: ClockAction['kind']): ClockAction | null => {
    if (!canFile) return null;
    const a = actions.find((x) => x.kind === kind);
    return a ? { kind, enabled: a.enabled, reason: a.reason } : null;
  };
  const entry = (slot: ClockSlot, e: Partial<ClockEntry>): ClockEntry => ({
    slot,
    label: SLOT_LABEL[slot],
    state: 'na',
    submittedAt: null,
    opensAt: null,
    dueAt: null,
    hint: '',
    action: null,
    ...e,
  });

  // ── Baseline ──
  const bWin = baselineWindow(cp);
  const bAt = submitted.baseline;
  let baseline: ClockEntry;
  if (bAt != null) {
    baseline = entry('baseline', { state: 'submitted', submittedAt: bAt, hint: 'accepted' });
  } else if (!bWin) {
    baseline = entry('baseline', { state: 'na', hint: 'opens at alignment' });
  } else if (now > bWin.closeAt) {
    baseline = entry('baseline', {
      state: 'overdue',
      dueAt: bWin.closeAt,
      hint: `window closed ${formatReportDate(bWin.closeAt)}`,
      action: actionFor('baseline'),
    });
  } else {
    const left = Math.max(0, Math.ceil((bWin.closeAt - now) / DAY));
    baseline = entry('baseline', {
      state: 'open',
      opensAt: bWin.openAt,
      dueAt: bWin.closeAt,
      hint: `${left} ${left === 1 ? 'day' : 'days'} left`,
      action: actionFor('baseline'),
    });
  }

  // ── Quarterlies: the first unsubmitted one owns the rolling window ──
  const qWin = quarterlyWindow(cp, subs);
  let firstOpenTaken = false;
  const quarterlies = Q_SLOTS.map((slot, i) => {
    const at = submitted[slot];
    if (at != null) return entry(slot, { state: 'submitted', submittedAt: at, hint: 'BP average + weight' });
    const prior = Q_SLOTS[i - 1];
    const previous = prior ? SLOT_LABEL[prior] : 'the baseline';
    if (firstOpenTaken || !qWin) {
      return entry(slot, { state: 'blocked', hint: `after ${previous}` });
    }
    firstOpenTaken = true;
    if (now < qWin.openAt) {
      return entry(slot, {
        state: 'upcoming',
        opensAt: qWin.openAt,
        dueAt: qWin.closeAt,
        hint: `due ${formatReportDate(qWin.closeAt)}`,
        action: actionFor('quarterly'),
      });
    }
    if (now > qWin.closeAt) {
      return entry(slot, {
        state: 'overdue',
        opensAt: qWin.openAt,
        dueAt: qWin.closeAt,
        hint: `window closed ${formatReportDate(qWin.closeAt)}`,
        action: actionFor('quarterly'),
      });
    }
    return entry(slot, {
      state: 'open',
      opensAt: qWin.openAt,
      dueAt: qWin.closeAt,
      hint: `due ${formatReportDate(qWin.closeAt)}`,
      action: actionFor('quarterly'),
    });
  });

  // ── Final ──
  const fWin = finalWindow(cp);
  const fAt = submitted.final;
  let final: ClockEntry;
  if (fAt != null) {
    final = entry('final', { state: 'submitted', submittedAt: fAt, hint: 'accepted' });
  } else if (!fWin) {
    final = entry('final', { state: 'na', hint: 'day 365 to day 425, after alignment' });
  } else {
    // ⚠️ CMS also allows "early success reporting" — the end-of-period report from 90 days before
    // day 365 for eCKM/CKM, 180 for BH/MSK (ACCESSReportTypeCS). We do NOT implement it: the submit
    // gate reads finalWindow(), which opens at +365, so naming the earlier date here would
    // advertise a filing the button refuses, on a track-dependent number nothing here knows.
    const hint = now < fWin.openAt
      ? `opens ${formatReportDate(fWin.openAt)} · closes ${formatReportDate(fWin.closeAt)}`
      : `closes ${formatReportDate(fWin.closeAt)}`;
    if (now > fWin.closeAt) {
      final = entry('final', {
        state: 'overdue', opensAt: fWin.openAt, dueAt: fWin.closeAt, hint, action: actionFor('final'),
      });
    } else if (now < fWin.openAt) {
      final = entry('final', {
        state: 'upcoming', opensAt: fWin.openAt, dueAt: fWin.closeAt, hint, action: actionFor('final'),
      });
    } else {
      final = entry('final', {
        state: 'open', opensAt: fWin.openAt, dueAt: fWin.closeAt, hint, action: actionFor('final'),
      });
    }
  }

  // ── Completion report: a task, not a CMS submission ──
  const pool = tasks || [];
  const doneAt = completedTemplateAt(pool, COMPLETION_TEMPLATE);
  const open = pool.find((t) => templateCodeOf(t) === COMPLETION_TEMPLATE && t?.status !== 'completed' && t?.status !== 'cancelled');
  let completion: ClockEntry;
  // Tri-state: null = not completed; undefined = completed with no usable date.
  if (doneAt !== null) {
    completion = entry('completion', {
      state: 'submitted', submittedAt: doneAt ?? null, hint: 'shared with the PCP',
    });
  } else if (open) {
    const due = ms(open.due_at);
    const late = isTaskOverdue(open, new Date(now));
    completion = entry('completion', {
      state: late ? 'overdue' : 'upcoming',
      dueAt: due,
      hint: due == null ? 'report to the PCP' : `${late ? 'overdue since' : 'due'} ${formatReportDate(due)}`,
    });
  } else {
    completion = entry('completion', { state: 'na', hint: 'created at the end of the period' });
  }

  // The ring and the clock must not print two verdicts about one period: `dayCounter` says
  // "Period closed" on a terminal status or past +425, so a countdown beside it would contradict
  // it. Submitted milestones keep their date — that filing did happen.
  const entries = [baseline, ...quarterlies, final, completion];
  if (dayCounter(cp, now).state !== 'closed') return entries;
  return entries.map((e) => (e.state === 'submitted' ? e : entry(e.slot, {
    state: 'na', hint: 'care period closed',
  })));
}
